/**
 * Core data model for House Builder.
 *
 * Guiding principle: "everything is a polygon at an elevation."
 * A sunken room (Room.floorOffset), a split-level wing (Level.baseElevation),
 * and a mezzanine all reuse the same machinery instead of special cases.
 *
 * Canonical units are FEET (floats). Display layers convert to ft+in.
 */

import type { ParamValues, StyleId } from './params'

export { type StyleId } from './params'

export const SCHEMA_VERSION = 2

/** A 2D point in plot space, in feet. Origin (0,0) is the plot's top-left corner. */
export interface Point {
  x: number
  y: number
}

export type PolygonPath = Point[]

// ---------------------------------------------------------------------------
// Project root
// ---------------------------------------------------------------------------

export interface Project {
  id: string
  name: string
  schemaVersion: number
  createdAt: number
  updatedAt: number
  settings: BuildingSettings
  plot: Plot
  levels: Level[]
  stairs: Stair[]
}

// ---------------------------------------------------------------------------
// Building-wide settings & standards
// ---------------------------------------------------------------------------

/** Editable defaults seeded from NBC-India / common South-Indian practice. */
export interface StandardsTable {
  exteriorWallThickness: number // ft (default 0.75 = 9 in)
  interiorWallThickness: number // ft (default 0.375 = 4.5 in)
  floorHeight: number // ft, floor-to-floor (default 10)
  ceilingHeight: number // ft (default 9.5)
  plinthHeight: number // ft (default 1.5)
  mainDoor: Dimensions2D // 3.5 x 7 ft
  internalDoor: Dimensions2D // 3 x 7 ft
  bathroomDoor: Dimensions2D // 2.5 x 6.5 ft
  window: Dimensions2D // 4 x 4 ft
  windowSillHeight: number // ft (default 3)
  stairRiser: number // ft (default 0.5 = 6 in)
  stairTread: number // ft (default ~0.92 = 11 in)
  stairWidth: number // ft (default 3.5)
}

export interface BuildingSettings {
  /** Master snap increment in feet (default 0.5 = 6 in). */
  gridSnap: number
  /** Standards / defaults table, all editable per project. */
  standards: StandardsTable
  /** Cross-cutting visual style applied to families. */
  style: StyleId
}

// ---------------------------------------------------------------------------
// Plot & outdoor zones
// ---------------------------------------------------------------------------

export interface Setbacks {
  front: number // +y edge (bottom in plan)
  rear: number // -y edge (top in plan)
  left: number // -x edge
  right: number // +x edge
  /** All in feet. */
}

export interface Plot {
  /** Land boundary polygon, feet. Defaults to a rectangle. */
  boundary: PolygonPath
  setbacks: Setbacks
  /** Optional compound wall ringing the plot boundary; owns the front gate. */
  compoundWall?: CompoundWall
}

/** A boundary wall around the plot. Its openings (the front gate) anchor to plot.boundary edges. */
export interface CompoundWall {
  enabled: boolean
  thickness: number // ft (default 0.5 = 6 in)
  height: number // ft (default ~4.5)
  /** Distance the wall centerline is inset inward from the plot boundary, feet (default 0). */
  inset: number
  openings: Opening[]
}

// ---------------------------------------------------------------------------
// Levels (floors), rooms, props
// ---------------------------------------------------------------------------

export interface Level {
  id: string
  name: string
  /** Finished-floor-level elevation relative to ground (=0), in feet. */
  baseElevation: number
  /** Floor-to-floor height for this level, in feet. */
  height: number
  /** Region of the plot this level occupies (a sub-polygon of the buildable footprint). */
  footprint: PolygonPath
  /** Groups split halves / mezzanines of the same "storey". */
  parentLevelId?: string
  /** Spaces (indoor rooms AND outdoor areas — see Space). */
  spaces: Space[]
  props: Prop[]
}

/**
 * How a space is enclosed. The two independent axes (walls + ceiling) express the
 * full vocabulary: room, covered area, terrace, portico, open outdoor surface.
 */
export type WallMode = 'full' | 'half' | 'none'

/** Named presets that set sensible wall/ceiling/surface defaults. Stored for clarity; the
 *  individual wallMode / hasCeiling fields remain the source of truth and stay editable. */
export type SpaceKindId =
  | 'room' // full walls + ceiling
  | 'coveredArea' // half walls + ceiling
  | 'terrace' // half walls + open top
  | 'portico' // no walls + ceiling (carport/portico)
  | 'openArea' // no walls + open top (garden, driveway, lawn, mud bed)

/** Floor / ground surface finishes (indoor + outdoor). Catalog lives in model/materials.ts. */
export type SurfaceId =
  // indoor
  | 'tile'
  | 'wood'
  | 'marble'
  | 'concrete'
  | 'carpet'
  | 'grippyTile'
  // outdoor
  | 'grass'
  | 'grassStonePath'
  | 'stonePath'
  | 'paver'
  | 'gravel'
  | 'mud'
  | 'water'
  | 'woodDeck'

/**
 * A bounded area on a level. Covers everything you draw — an enclosed room, a terrace,
 * a portico, or an open outdoor surface like a garden — distinguished by wallMode + hasCeiling.
 */
export interface Space {
  id: string
  name: string
  polygon: PolygonPath
  /** Preset this space was created from (drives defaults; not authoritative). */
  kind: SpaceKindId
  wallMode: WallMode
  hasCeiling: boolean
  surface: SurfaceId
  /** Vertical offset of this space's floor vs its level (e.g. -3 for a sunken hall). */
  floorOffset: number
  /** Overrides level height if set (feet). */
  ceilingHeight?: number
  /** Label position override in 2D (feet); auto-centroid if absent. */
  labelPos?: Point
  /** Doors / windows cut into this space's walls (edge-anchored — see Opening). */
  openings: Opening[]
  /**
   * Indices of polygon edges (vertex i → i+1) that have NO wall — the space is open on those
   * sides (e.g. a dining open to the hall, a 3-walled parking). Empty/absent = all edges walled.
   * Applies on top of `wallMode`: `none` → no walls regardless; `full`/`half` → walls on every
   * edge EXCEPT those listed here.
   */
  openEdges?: number[]
}

// ---------------------------------------------------------------------------
// Walls & openings (GENERATED from room polygons — not authored by hand)
// ---------------------------------------------------------------------------

export interface Wall {
  id: string
  /** Centerline path of the wall segment, feet. */
  path: PolygonPath
  thickness: number // ft
  isExterior: boolean
  openings: Opening[]
}

/**
 * A door, window, or gate cut into a wall — now part of the families system.
 *
 * `familyId` ∈ 'door' | 'window' | 'gate'. `params` carries ALL sizing and behaviour
 * (width, height, sillHeight, swing, hinge, kinematics, …). Edge-anchoring is placement;
 * it stays flat on the Opening.
 */
export interface Opening {
  id: string
  /** Family this opening belongs to: 'door' | 'window' | 'gate' (+ future). */
  familyId: string
  /** Which preset was used to create it (for display / reset). */
  presetId?: string
  /** Edge i of the owner polygon: vertex i → i+1. */
  edgeIndex: number
  /** Feet from the edge-start vertex to the opening CENTER, measured along the edge. */
  offset: number
  /**
   * All sizing + kinematics in one bag:
   *   width, height, sillHeight (numbers, ft)
   *   swing ('in'|'out'), hinge ('left'|'right')
   *   leaves ('1'|'2'), kinematics ('swing'|'sliding'|'biparting')  [gate]
   *   panel ('flush'|'panel'|'glazed')                              [door]
   *   style ('plain'|'grid')                                        [window]
   *   style ('simple'|'grand')                                      [gate]
   */
  params: ParamValues
}

// ---------------------------------------------------------------------------
// Props (parametric family instances)
// ---------------------------------------------------------------------------

/**
 * A placed prop — now a parametric family instance.
 *
 * `familyId` identifies which family (bed, sofa, …) and `params` drives everything:
 * width, depth, height, and any family-specific semantic params (size enum, shape, …).
 * `family.dimensions(params)` computes the bounding box used for 2D + 3D placement.
 */
export interface Prop {
  id: string
  /** Family id, e.g. 'bed', 'sofa', 'sink'. */
  familyId: string
  /** Preset used to create this prop (informational; params are the source of truth). */
  presetId?: string
  /** All sizing + semantic params (width, depth, height + family-specific keys). */
  params: ParamValues
  /** Center position of the prop's footprint, feet. */
  position: Point
  /** Rotation in degrees, clockwise in plan. */
  rotation: number
  /** Optional custom label (default: family/preset label). */
  label?: string
}

// ---------------------------------------------------------------------------
// Helpers kept for migration reference (not used in runtime anymore)
// ---------------------------------------------------------------------------

/**
 * Old prop type union — kept as a string-literal type so migrate.ts can reference it
 * for mapping old `type` values to the new `familyId`.
 */
export type LegacyPropType =
  | 'bed' | 'sofa' | 'chair' | 'table' | 'toilet' | 'sink' | 'shower'
  | 'kitchenCounter' | 'tv' | 'wardrobe' | 'car' | 'bike'
  | 'door' | 'window' | 'stairs'

/** Old opening kind — kept for migration mapping. */
export type LegacyOpeningKind = 'door' | 'window' | 'gate'

export interface Dimensions2D {
  width: number // ft (x)
  depth: number // ft (y)
}

// ---------------------------------------------------------------------------
// Stairs (connect levels; carry explicit direction — replaces "arrow clutter")
// ---------------------------------------------------------------------------

export interface Stair {
  id: string
  fromLevelId: string
  toLevelId: string
  /** Run path (polyline) in plan, feet — start is the bottom of the run. */
  path: PolygonPath
  /** Heading the climber faces, degrees clockwise; arrow derives from path + this. */
  direction: number
  riser: number // ft
  tread: number // ft
  width: number // ft
}
